// unit-switcher.js (Updated for mega menu sizes)

// --- Configuration ---
const INCH_TO_CM_FACTOR = 2.54; // 1 inch = 2.54 cm
const FT_TO_INCH_FACTOR = 12;   // 1 foot = 12 inches

// Selector for your unit selector buttons/options
const UNIT_SELECTOR_CONTAINER_SELECTOR = '.multi-selectors__item:has(at-disclosure[data-disclosure-units])';
const UNIT_OPTION_LINK_SELECTOR = 'a.disclosure-list__option';

// Selector for elements that display a size on product/collection pages (data-original-raw assumed)
const SIZE_ELEMENTS_SELECTOR = '[data-original-raw][data-original-unit]';

// Selector for size filter text elements
const FILTER_SIZE_TEXT_SELECTOR = '.tag__text';
const FILTER_SIZE_INPUT_SELECTOR = '.tag__input[name="filter.p.m.option.size"]'; // Targeting the checkbox input with the value

// Selector for desktop mega menu size links
// We exclude the top-level 'SIZES & SHAPES' link as it's not a dimension.
const MEGA_MENU_SIZE_LINK_SELECTOR = '.megamenu__col a.site-nav__dropdown-link:not(.site-nav__dropdown-link--top-level)';

// NEW: Selector for mobile menu size links
// These are the <a> tags within the mobile menu that display sizes.
// We target the <span> inside the <a> for text content, but the <a> itself for the data attribute.
const MOBILE_MENU_SIZE_LINK_SELECTOR = '.slide-nav__dropdown a.slide-nav__link span';


// Key for localStorage to store the user's preferred unit
const PREFERRED_UNIT_STORAGE_KEY = 'preferredUnit';

// --- Global flag to control form submission ---
let preventLocalizationFormSubmit = false; // Initialize flag

// --- Helper Functions ---

/**
 * Converts a feet.decimal_inches value (e.g., 7.11 where .11 means 11 inches) into total inches.
 * @param {number} ftPart - The feet part (e.g., 7 from 7.11).
 * @param {number} decInchesPart - The decimal part representing inches (e.g., 11 from 7.11).
 * @returns {number} Total inches.
 */
function ftAndDecInchesToTotalInches(ftPart, decInchesPart) {
    return (ftPart * FT_TO_INCH_FACTOR) + decInchesPart;
}

/**
 * Converts total inches into an object with feet and inches.
 * @param {number} totalInches - The total inches.
 * @returns {{feet: number, inches: number}} Object with feet and remaining inches.
 */
function totalInchesToFtAndInches(totalInches) {
    const feet = Math.floor(totalInches / FT_TO_INCH_FACTOR);
    const inches = Math.round(totalInches % FT_TO_INCH_FACTOR); // Round inches for display
    return { feet, inches };
}

/**
 * Parses a dimension string like "7.11" or "7' 11\"" or "7' x 10'" into its feet and inches components
 * (assuming decimal part or quotes represent inches).
 *
 * IMPORTANT: This function assumes that for "X.Y", the ".Y" part represents inches (e.g., "7.11" means 7 feet 11 inches).
 * This is consistent with how your theme typically handles `data-original-raw` values.
 *
 * @param {string} dimensionStr - The dimension string (e.g., "7.11", "7' 11"", "7' x 10'").
 * @returns {{ft: number, inches: number}|null} Object with ft and inches, or null if invalid.
 */
function parseFtDecimalInches(dimensionStr) {
    // Handle "X' Y\"" format
    const quoteRegex = /(\d+)'\s*(\d*)"?/;
    let match = dimensionStr.match(quoteRegex);
    if (match) {
        const ft = parseInt(match[1], 10);
        const inches = match[2] ? parseInt(match[2], 10) : 0;
        if (!isNaN(ft) && !isNaN(inches)) {
            return { ft, inches };
        }
    }

    // Handle "X.Y" format (e.g., 7.11) where .Y is inches
    const decimalRegex = /(\d+)\.(\d+)/;
    match = dimensionStr.match(decimalRegex);
    if (match) {
        const ft = parseInt(match[1], 10);
        const inches = parseInt(match[2], 10); // Assume decimal part directly represents inches
        if (!isNaN(ft) && !isNaN(inches)) {
            return { ft, inches };
        }
    }

    // Handle single foot string "X'"
    const singleFootRegex = /(\d+)'/;
    match = dimensionStr.match(singleFootRegex);
    if (match) {
        const ft = parseInt(match[1], 10);
        if (!isNaN(ft)) {
            return { ft, inches: 0 };
        }
    }

    // Handle cases like "Runner", "Round", "Square", "Oval" or any string without digits - return null
    if (!dimensionStr.match(/\d/)) {
        return null;
    }

    // Fallback for simple numbers like "7" or "2" (assuming they mean 7 feet, 2 feet)
    const singleNum = parseFloat(dimensionStr);
    if (!isNaN(singleNum)) {
        // If it's a simple number without feet/inch markers, treat it as feet, 0 inches
        return { ft: Math.floor(singleNum), inches: Math.round((singleNum % 1) * FT_TO_INCH_FACTOR) };
    }

    return null; // Could not parse
}

/**
 * Converts a single dimension (e.g., total inches) between units.
 * @param {number} totalInches - The dimension in total inches.
 * @param {string} toUnit - The unit to convert to ('ft/in' or 'cm').
 * @returns {number} The converted value (total inches for 'ft/in', or cm for 'cm').
 */
function convertDimension(totalInches, toUnit) {
    if (toUnit === 'cm') {
        return totalInches * INCH_TO_CM_FACTOR;
    }
    // If toUnit is 'ft/in', it means we want total inches, which is our base.
    return totalInches;
}

/**
 * Formats a single dimension value for display.
 * @param {number} value - The numeric value (either total inches or cm).
 * @param {string} unitType - The target display unit ('ft/in' or 'cm').
 * @returns {string} The formatted dimension string (e.g., "7' 11"", "241cm").
 */
function formatDimension(value, unitType) {
    if (unitType === 'cm') {
        return `${Math.round(value)} cm`; // Display CM rounded to nearest whole number
    } else if (unitType === 'ft/in') {
        const { feet, inches } = totalInchesToFtAndInches(value);
        if (inches === 0) {
            return `${feet}'`; // Display as 9' if inches are 0
        }
        return `${feet}' ${inches}"`; // Changed to 9' 10" format
    }
    return String(value); // Fallback
}

/**
 * Retrieves the preferred unit from localStorage.
 * @returns {string} The preferred unit ('ft/in' or 'cm'), defaults to 'ft/in'.
 */
function getPreferredUnit() {
    return localStorage.getItem(PREFERRED_UNIT_STORAGE_KEY) || 'ft/in';
}

/**
 * Saves the preferred unit to localStorage.
 * @param {string} unit - The unit to save ('ft/in' or 'cm').
 */
function setPreferredUnit(unit) {
    localStorage.setItem(PREFERRED_UNIT_STORAGE_KEY, unit);
}

/**
 * Helper function to process and update a single dimension-displaying HTML element.
 * It will store the original text content in a data attribute if not already present.
 * @param {HTMLElement} element - The HTML element to update (e.g., span, a).
 * @param {string} targetUnit - The unit to display ('ft/in' or 'cm').
 */
function processDimensionElement(element, targetUnit) {
    // Get the original raw dimension string from data attribute or text content
    let originalRaw = element.dataset.originalRaw;

    // If data-original-raw is not set (e.g., for newly loaded menu links), capture it.
    // We trim " ft" from the end of menu link text for consistent parsing.
    if (!originalRaw) {
        originalRaw = element.textContent.trim();
        // For menu links, remove " ft" from the end if present, for cleaner parsing
        // This applies to both MEGA_MENU_SIZE_LINK_SELECTOR and MOBILE_MENU_SIZE_LINK_SELECTOR
        if (element.matches(MEGA_MENU_SIZE_LINK_SELECTOR) || element.matches(MOBILE_MENU_SIZE_LINK_SELECTOR)) {
            originalRaw = originalRaw.replace(/\s*ft$/, '').trim();
        }
        element.dataset.originalRaw = originalRaw; // Store it for future conversions
    }

    // Check for non-numeric shape descriptors (e.g., "Runner", "Round Rugs")
    if (!originalRaw.match(/\d/)) {
        // Just set the text to its original non-converted form
        element.textContent = originalRaw;
        element.dataset.currentUnit = 'n/a'; // Mark as not unit-convertible
        return;
    }

    // Split the raw string (e.g., "7.11 x 4.3" or "2.5 x 4") into individual dimensions
    const dimensions = originalRaw.split('x').map(s => s.trim());
    let formattedDimensions = [];
    let isConvertible = false; // Flag to check if any dimension was actually converted

    for (const dimStr of dimensions) {
        const parsed = parseFtDecimalInches(dimStr);
        if (parsed) {
            const totalInches = ftAndDecInchesToTotalInches(parsed.ft, parsed.inches);
            const convertedValue = convertDimension(totalInches, targetUnit);
            formattedDimensions.push(formatDimension(convertedValue, targetUnit));
            isConvertible = true;
        } else {
            formattedDimensions.push(dimStr); // Keep original part if parsing fails
        }
    }

    if (isConvertible) { // Only update if conversion actually happened
        element.textContent = formattedDimensions.join(' x ');
        element.dataset.currentUnit = targetUnit;
    } else {
        // If no part was convertible, revert to original raw and mark as non-convertible
        element.textContent = originalRaw; // Use the stored originalRaw
        element.dataset.currentUnit = 'n/a';
    }
}


/**
 * Updates all size elements on the page to the specified target unit.
 * @param {string} targetUnit - The unit to display ('ft/in' or 'cm').
 */
function updateAllSizes(targetUnit) {
    // 1. Update product/display sizes (elements with data-original-raw from Liquid)
    document.querySelectorAll(SIZE_ELEMENTS_SELECTOR).forEach(element => {
        processDimensionElement(element, targetUnit);
    });

    // 2. Update size filter options
    document.querySelectorAll(FILTER_SIZE_TEXT_SELECTOR).forEach(textElement => {
        // Find the associated checkbox input to get its original value
        const parentLabel = textElement.closest('label.tag__checkbox-wrapper');
        if (!parentLabel) return;

        const inputElement = parentLabel.querySelector(FILTER_SIZE_INPUT_SELECTOR);
        if (!inputElement) return;

        // Create a temporary element to hold the input's value as originalRaw
        const tempElement = document.createElement('span');
        tempElement.dataset.originalRaw = inputElement.value; // Use input's value as original raw
        tempElement.dataset.originalUnit = 'ft.in'; // Assume original is ft.in for consistency

        processDimensionElement(tempElement, targetUnit); // Process using the general function

        // Update the visible text of the filter option
        textElement.textContent = tempElement.textContent;
    });

    // 3. Update desktop mega menu size links
    document.querySelectorAll(MEGA_MENU_SIZE_LINK_SELECTOR).forEach(linkElement => {
        // processDimensionElement will capture data-original-raw if not already set
        processDimensionElement(linkElement, targetUnit);
    });

    // 4. NEW: Update mobile menu size links
    document.querySelectorAll(MOBILE_MENU_SIZE_LINK_SELECTOR).forEach(linkElement => {
        // processDimensionElement will capture data-original-raw if not already set
        processDimensionElement(linkElement, targetUnit);
    });

    // 5. Update the visible text in ALL unit selector buttons on the page
    document.querySelectorAll(UNIT_SELECTOR_CONTAINER_SELECTOR).forEach(container => {
        const disclosureToggle = container.querySelector('.disclosure__toggle .disclosure-list__label');
        if (disclosureToggle) {
            disclosureToggle.textContent = targetUnit === 'ft/in' ? 'FT' : 'CM';
        }
    });
}

// --- Event Listeners and Initialization ---

document.addEventListener('DOMContentLoaded', () => {
    const localizationForm = document.querySelector('form[data-disclosure-form]');

    // IMPORTANT: Add submit listener to the localization form itself
    if (localizationForm) {
        localizationForm.addEventListener('submit', (event) => {
            if (preventLocalizationFormSubmit) {
                event.preventDefault(); // Prevent the form from submitting
                preventLocalizationFormSubmit = false; // Reset the flag
                console.log('Localization form submission prevented by unit switcher.');
            }
        });
    } else {
        console.warn('Localization form with data-disclosure-form not found. Form submission prevention for units may not work.');
    }

    const unitSelectorContainers = document.querySelectorAll(UNIT_SELECTOR_CONTAINER_SELECTOR);

    if (unitSelectorContainers.length > 0) {
        unitSelectorContainers.forEach(unitSelectorContainer => {
            const unitOptionLinks = unitSelectorContainer.querySelectorAll(UNIT_OPTION_LINK_SELECTOR);
            unitOptionLinks.forEach(clickedLink => {
                clickedLink.addEventListener('click', (event) => {
                    event.preventDefault(); // Prevent default link behavior (e.g., navigating to #)

                    const selectedUnitText = clickedLink.textContent.trim().toLowerCase();
                    let newUnit = getPreferredUnit();

                    if (selectedUnitText.includes('cm')) {
                        newUnit = 'cm';
                    } else if (selectedUnitText.includes('ft') || selectedUnitText.includes('in')) {
                        newUnit = 'ft/in';
                    }

                    // Only update if the unit actually changed
                    if (newUnit !== getPreferredUnit()) {
                        // Set the flag *before* processing the unit change,
                        // as the at-disclosure might trigger form submit immediately after input update.
                        preventLocalizationFormSubmit = true;

                        // Manually update the hidden input's value for the at-disclosure component
                        const unitPreferenceInput = unitSelectorContainer.querySelector('#unitPreferenceInput');
                        if (unitPreferenceInput) {
                            unitPreferenceInput.value = newUnit === 'ft/in' ? 'ft' : 'cm';
                            // Dispatch a change event on the input to mimic user interaction
                            // This might be what at-disclosure listens for to trigger form submission
                            unitPreferenceInput.dispatchEvent(new Event('change', { bubbles: true }));
                        }

                        setPreferredUnit(newUnit);
                        updateAllSizes(newUnit);
                        console.log(`Unit switched to: ${newUnit}`);
                    }
                });
            });
        });

        // Initial update when the page loads, based on saved preference
        const initialUnit = getPreferredUnit();
        updateAllSizes(initialUnit);

    } else {
        console.warn('Unit selector containers not found. Unit switching functionality may not work.');
    }

    // --- MutationObserver for dynamically loaded content ---
    // This part is crucial for Single Page Application (SPA) behavior where content might load after DOMContentLoaded
    // For example, when applying filters or navigating dynamically without a full page reload.
    const observerTarget = document.body; // Observe the entire body for changes
    const observer = new MutationObserver((mutationsList, observer) => {
        const currentUnit = getPreferredUnit(); // Get current unit once for efficiency

        for (const mutation of mutationsList) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === 1) { // Check if it's an element node
                        // Process existing elements within the added node
                        node.querySelectorAll(SIZE_ELEMENTS_SELECTOR).forEach(el => processDimensionElement(el, currentUnit));
                        node.querySelectorAll(FILTER_SIZE_TEXT_SELECTOR).forEach(textElement => {
                            const parentLabel = textElement.closest('label.tag__checkbox-wrapper');
                            if (parentLabel) {
                                const inputElement = parentLabel.querySelector(FILTER_SIZE_INPUT_SELECTOR);
                                if (inputElement) {
                                    const tempElement = document.createElement('span');
                                    tempElement.dataset.originalRaw = inputElement.value;
                                    tempElement.dataset.originalUnit = 'ft.in';
                                    processDimensionElement(tempElement, currentUnit);
                                    textElement.textContent = tempElement.textContent;
                                }
                            }
                        });
                        node.querySelectorAll(MEGA_MENU_SIZE_LINK_SELECTOR).forEach(el => processDimensionElement(el, currentUnit));
                        node.querySelectorAll(MOBILE_MENU_SIZE_LINK_SELECTOR).forEach(el => processDimensionElement(el, currentUnit)); // NEW: For mobile menu links

                        // Also process the added node itself if it matches a selector
                        if (node.matches(SIZE_ELEMENTS_SELECTOR)) processDimensionElement(node, currentUnit);
                        if (node.matches(FILTER_SIZE_TEXT_SELECTOR)) {
                            const parentLabel = node.closest('label.tag__checkbox-wrapper');
                            if (parentLabel) {
                                const inputElement = parentLabel.querySelector(FILTER_SIZE_INPUT_SELECTOR);
                                if (inputElement) {
                                    const tempElement = document.createElement('span');
                                    tempElement.dataset.originalRaw = inputElement.value;
                                    tempElement.dataset.originalUnit = 'ft.in';
                                    processDimensionElement(tempElement, currentUnit);
                                    node.textContent = tempElement.textContent;
                                }
                            }
                        }
                        if (node.matches(MEGA_MENU_SIZE_LINK_SELECTOR)) processDimensionElement(node, currentUnit);
                        if (node.matches(MOBILE_MENU_SIZE_LINK_SELECTOR)) processDimensionElement(node, currentUnit); // NEW: For mobile menu links


                        // Check for new unit selector containers and attach listeners to their links
                        const newlyAddedContainers = node.matches(UNIT_SELECTOR_CONTAINER_SELECTOR) ? [node] : Array.from(node.querySelectorAll(UNIT_SELECTOR_CONTAINER_SELECTOR));
                        newlyAddedContainers.forEach(container => {
                            const newUnitOptionLinks = container.querySelectorAll(UNIT_OPTION_LINK_SELECTOR);
                            newUnitOptionLinks.forEach(clickedLink => {
                                if (!clickedLink.__unitSwitcherListenerAdded) { // Custom flag to prevent duplicate listeners
                                    clickedLink.addEventListener('click', (event) => {
                                        event.preventDefault();

                                        const selectedUnitText = clickedLink.textContent.trim().toLowerCase();
                                        let newUnit = getPreferredUnit();

                                        if (selectedUnitText.includes('cm')) {
                                            newUnit = 'cm';
                                        } else if (selectedUnitText.includes('ft') || selectedUnitText.includes('in')) {
                                            newUnit = 'ft/in';
                                        }

                                        if (newUnit !== getPreferredUnit()) {
                                            preventLocalizationFormSubmit = true; // Set flag for dynamic elements too
                                            const unitPreferenceInput = container.querySelector('#unitPreferenceInput');
                                            if (unitPreferenceInput) {
                                                unitPreferenceInput.value = newUnit === 'ft/in' ? 'ft' : 'cm';
                                                unitPreferenceInput.dispatchEvent(new Event('change', { bubbles: true }));
                                            }
                                            setPreferredUnit(newUnit);
                                            updateAllSizes(newUnit);
                                            console.log(`Unit switched to: ${newUnit} (dynamic)`);
                                        }
                                    });
                                    clickedLink.__unitSwitcherListenerAdded = true; // Mark as having listener
                                }
                            });
                        });
                    }
                }
            }
        }
    });

    // Start observing the DOM for changes
    observer.observe(observerTarget, { childList: true, subtree: true });
});